import { cx } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import { useSelectedCourse, useCourseBuilder, getAllCoursesQuery, useToggleBlockMutation, BlockTypes, showOnboardingOnMount, courseBuilderStore } from '@pm2/course-builder';
import { pm2ConfigStore } from '@pm2/store';
import { useNotifier } from '@provider/notifier';
import { CSSRefTransition } from '@utils/react';
import { buttonCls, ENTER_TRANSITION_DURATION_MS, outlineCls, overlayCls, dialogCls, dialogButtonCls, simplePlusCls } from './atom.add-to-course-builder.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

/**
 * button that allows teachers to add elements into their course builder
 * palette when it's enabled.
 */
function AddToCourseBuilder(props) {
  const selectedCourse = useSelectedCourse();
  const notifier = useNotifier();
  const buttonRef = useRef(null);
  const portalConfig = pm2ConfigStore.useSelector(state => state.config);
  const elementBlock = resolveBlock(portalConfig, props.block);
  const isAddedToBasket = selectedCourse?.blocks?.find(block => block.id === elementBlock.id);
  const isEnabled = useCourseBuilder(state => state.showPanel);
  const courses = useQuery(getAllCoursesQuery.useQueryConfig({
    queryConfig: {
      enabled: isEnabled
    }
  }));
  const toggleBlock = useToggleBlockMutation();
  const handleClick = evt => {
    evt.stopPropagation();
    evt.preventDefault();

    // If the user hasn't yet interacted with the onboarding screen then we
    // cannot do anything on click.
    if (showOnboardingOnMount()) {
      notifier.dialog({
        className: dialogCls,
        heading: "Færdiggør onboardingen før du tilføjer indhold",
        body: "Du skal færdiggøre onboardingen i panelet til højre før du kan tilføje indhold til forløb. Dette kan du gøre ved enten at klikke på 'Spring over' eller 'Næste/Start'.",
        actions: [{
          intent: "approve",
          label: /*#__PURE__*/jsx("span", {
            className: dialogButtonCls,
            children: "Ok"
          }),
          type: "primary"
        }]
      });
      return;
    }

    // Prompt user to select a course if none has currently been chosen
    if (!selectedCourse) {
      if (!courses.data?.length) {
        promptWithoutExistingCourses(notifier);
      } else {
        promptWithExistingCourses(notifier);
      }
      return;
    }
    toggleBlock.mutate({
      course: selectedCourse,
      block: elementBlock
    });
  };
  useEffect(() => {
    buttonRef.current?.parentElement?.classList.toggle("addToBasketOpen", isEnabled);
  }, [isEnabled]);
  return /*#__PURE__*/jsx(CSSRefTransition, {
    in: isEnabled,
    timeout: ENTER_TRANSITION_DURATION_MS,
    classNames: buttonCls,
    nodeRef: buttonRef,
    mountOnEnter: true,
    unmountOnExit: true,
    children: ref => /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("button", {
        ref: ref,
        className: cx(buttonCls, "add-to-basket", props.className, isAddedToBasket && "added", props.small && "small"),
        onClick: handleClick,
        "aria-label": isAddedToBasket ? `Fjern "${elementBlock.title}" fra kurv` : `Tilføj "${elementBlock.title}" til kurv`,
        children: /*#__PURE__*/jsx("span", {
          className: outlineCls
        })
      }), !props.withoutOverlay && /*#__PURE__*/jsx("div", {
        className: overlayCls
      })]
    })
  });
}
function resolveBlock(portalConfig, block) {
  switch (block.type) {
    case BlockTypes.REFERENCE:
      return {
        type: BlockTypes.REFERENCE,
        id: `PM2-${block.element.id}-${portalConfig.id}`,
        title: block.element.title,
        note: {
          content: "",
          enabled: false
        },
        element: {
          id: block.element.id,
          title: block.element.title,
          type: block.element.type
        },
        product: {
          id: portalConfig.id,
          title: portalConfig.title,
          type: "PM2"
        }
      };
    case BlockTypes.LINK:
      return {
        type: BlockTypes.LINK,
        id: `PM2-${block.id}-${portalConfig.id}`,
        title: block.title,
        href: block.href,
        description: block.description ?? "",
        note: {
          content: "",
          enabled: false
        }
      };
    case BlockTypes.YOUTUBE:
      return {
        type: BlockTypes.YOUTUBE,
        id: `PM2-${block.id}-${portalConfig.id}`,
        title: block.title,
        videoUrl: block.videoUrl,
        note: {
          content: "",
          enabled: false
        }
      };
  }
}
async function promptWithExistingCourses(notifier) {
  const result = await notifier.dialog({
    className: dialogCls,
    heading: "Vælg et forløb for at tilføje indhold",
    body: "Vælg blandt dine eksisterende forløb eller opret et nyt forløb for at tilføje indhold.",
    actions: [{
      intent: "create",
      label: /*#__PURE__*/jsxs("span", {
        className: dialogButtonCls,
        children: [/*#__PURE__*/jsx("span", {
          className: simplePlusCls
        }), " ", /*#__PURE__*/jsx("span", {
          children: "Opret nyt forl\xF8b"
        })]
      }),
      type: "primary"
    }, {
      intent: "choose",
      label: "Vælg eksisterende forløb",
      type: "primary"
    }]
  });
  switch (result) {
    case "create":
      courseBuilderStore.dispatch(courseBuilderStore.actions.toggleCreateDrawer(true));
      break;
    case "choose":
      courseBuilderStore.dispatch(courseBuilderStore.actions.toggleCreateDrawer(false));
      break;
    // no-op
  }
}
async function promptWithoutExistingCourses(notifier) {
  const result = await notifier.dialog({
    className: dialogCls,
    heading: "Vælg et forløb for at tilføje indhold",
    body: "Vælg blandt dine eksisterende forløb eller opret et nyt forløb for at tilføje indhold.",
    actions: [{
      intent: "create",
      label: /*#__PURE__*/jsxs("span", {
        className: dialogButtonCls,
        children: [/*#__PURE__*/jsx("span", {
          className: simplePlusCls
        }), " ", /*#__PURE__*/jsx("span", {
          children: "Opret nyt forl\xF8b"
        })]
      }),
      type: "primary"
    }]
  });
  switch (result) {
    case "create":
      courseBuilderStore.dispatch(courseBuilderStore.actions.toggleCreateDrawer(true));
      break;
    // no-op
  }
}

export { AddToCourseBuilder };
//# sourceMappingURL=atom.add-to-course-builder.js.map
